'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useTheme } from 'next-themes';
import { Building2, GraduationCap, Menu, Moon, Sun, Users } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from '@/components/ui/sheet';
import { LanguageToggle } from '@/components/ui/language-toggle';
import { useTranslations } from '@/i18n/I18nProvider';

function MobileNavSheet() {
    const [mounted, setMounted] = useState(false);
    const [open, setOpen] = useState(false);
    const [hfuUrl, setHfuUrl] = useState('https://hfu.digital');
    const { t } = useTranslations('nav');
    const { t: tBrand } = useTranslations('brand');

    useEffect(() => {
        setMounted(true);
        const hostname = window.location.hostname;
        if (hostname.startsWith('dev.') || hostname.includes('.dev.')) {
            setHfuUrl('https://dev.hfu.digital');
        }
    }, []);

    const handleLinkClick = () => {
        setOpen(false);
    };

    if (!mounted) {
        return (
            <Button variant="ghost" size="icon" className="h-9 w-9 md:hidden">
                <Menu className="h-5 w-5" />
                <span className="sr-only">{t('openMenu')}</span>
            </Button>
        );
    }

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9 md:hidden">
                    <Menu className="h-5 w-5" />
                    <span className="sr-only">{t('openMenu')}</span>
                </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[350px]">
                <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                        <a href={hfuUrl} className="flex items-center gap-2 group">
                            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple dark:bg-purple-bright">
                                <span className="text-lg font-bold text-white">H</span>
                            </div>
                            <span className="group-hover:underline">{tBrand('hfuDigital')}</span>
                        </a>
                        <span className="text-gray-400 dark:text-white/40">/</span>
                        <Link href="/" onClick={handleLinkClick} className="group">
                            <span className="group-hover:underline">{tBrand('betterSplan')}</span>
                        </Link>
                    </SheetTitle>
                </SheetHeader>

                <nav className="mt-6 flex flex-col gap-1">
                    {/* Programs */}
                    <Link
                        href="/programs"
                        onClick={handleLinkClick}
                        className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium hover:bg-gray-100 dark:hover:bg-white/5"
                    >
                        <GraduationCap className="h-4 w-4" />
                        {t('programs')}
                    </Link>

                    {/* Rooms */}
                    <Link
                        href="/rooms"
                        onClick={handleLinkClick}
                        className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium hover:bg-gray-100 dark:hover:bg-white/5"
                    >
                        <Building2 className="h-4 w-4" />
                        {t('rooms')}
                    </Link>

                    {/* Teachers */}
                    <Link
                        href="/teachers"
                        onClick={handleLinkClick}
                        className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium hover:bg-gray-100 dark:hover:bg-white/5"
                    >
                        <Users className="h-4 w-4" />
                        {t('teachers')}
                    </Link>
                </nav>
            </SheetContent>
        </Sheet>
    );
}

export function PublicHeader() {
    const { resolvedTheme, setTheme } = useTheme();
    const [mounted, setMounted] = useState(false);
    const [hfuUrl, setHfuUrl] = useState('https://hfu.digital');
    const { t } = useTranslations('nav');
    const { t: tBrand } = useTranslations('brand');

    useEffect(() => {
        setMounted(true);
        const hostname = window.location.hostname;
        if (hostname.startsWith('dev.') || hostname.includes('.dev.')) {
            setHfuUrl('https://dev.hfu.digital');
        }
    }, []);

    return (
        <header className="sticky top-0 z-50 border-b backdrop-blur-md border-gray-200 bg-white/80 dark:border-white/5 dark:bg-[#0a0a0f]/80">
            <div className="container mx-auto px-6 lg:px-8 max-w-7xl">
                <div className="flex h-16 items-center justify-between">
                    {/* Logo/Branding - Left */}
                    <div className="flex items-center space-x-2 min-w-0">
                        <a href={hfuUrl} className="flex items-center space-x-2 group">
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-purple dark:bg-purple-bright transition-transform group-hover:scale-105">
                                <span className="text-lg font-bold text-white">H</span>
                            </div>
                            <span className="hidden sm:inline text-xl font-bold text-gray-900 dark:text-white font-heading group-hover:underline">
                                {tBrand('hfuDigital')}
                            </span>
                        </a>
                        <span className="hidden sm:inline text-gray-400 dark:text-white/40">/</span>
                        <Link href="/" className="group min-w-0 truncate">
                            <span className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white group-hover:underline">
                                {tBrand('betterSplan')}
                            </span>
                        </Link>
                    </div>

                    {/* Center Navigation - Hidden on mobile */}
                    <nav className="hidden md:flex items-center gap-1">
                        {/* Programs Link */}
                        <Link href="/programs">
                            <Button
                                variant="ghost"
                                className="h-9 px-3 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-white/60 dark:hover:text-white"
                            >
                                <GraduationCap className="h-4 w-4 mr-1.5" />
                                {t('programs')}
                            </Button>
                        </Link>

                        {/* Rooms Link */}
                        <Link href="/rooms">
                            <Button
                                variant="ghost"
                                className="h-9 px-3 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-white/60 dark:hover:text-white"
                            >
                                <Building2 className="h-4 w-4 mr-1.5" />
                                {t('rooms')}
                            </Button>
                        </Link>

                        {/* Teachers Link */}
                        <Link href="/teachers">
                            <Button
                                variant="ghost"
                                className="h-9 px-3 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-white/60 dark:hover:text-white"
                            >
                                <Users className="h-4 w-4 mr-1.5" />
                                {t('teachers')}
                            </Button>
                        </Link>
                    </nav>

                    {/* Right Section */}
                    <div className="flex items-center space-x-2">
                        {/* Language Toggle */}
                        <LanguageToggle />

                        {/* Theme Toggle */}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
                            className="h-9 w-9"
                            suppressHydrationWarning
                        >
                            {mounted && (
                                <>
                                    <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
                                    <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                                </>
                            )}
                            <span className="sr-only">{t('toggleTheme')}</span>
                        </Button>

                        {/* Mobile Navigation */}
                        <MobileNavSheet />
                    </div>
                </div>
            </div>
        </header>
    );
}
